import {
  BaseConfig,
  envType,
  MgPrisConfig,
  OrgUnitType,
  SanityConfig,
} from '@retail/mgpris/config-types';

const env = (process.env['NX_PUBLIC_REACT_APP_ENV'] || 'dev') as envType;
const version = process.env['NX_PUBLIC_REACT_APP_VERSION'] || 'local';
const buildTime = process.env['NX_PUBLIC_BUILD_TIMESTAMP'];

const isRunningLocally = version === 'local';

const baseConfig: BaseConfig = {
  env,
  version,
  buildTime: buildTime ? Number(buildTime) : undefined,
  appInfo: {
    name: 'MG Pris',
    key: 'mgpris',
  },
  country: 'SE',
  sentryDsn: 'https://e3cd88ae61f54917bb65c433bc5f7317@o344472.ingest.sentry.io/4504948992573440',
  muiLicenseKey:
    'bebbb7e316354130b2d89b644169cd7bTz0xMjEzMjEsRT0xNzkzODM2Nzk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y',
  retailerLogoBaseUrl: 'https://www.byggmaterialpriser.se/static/images/logos',
};

const sanityDevConfig: SanityConfig = {
  projectId: 'fqqjuanq',
  dataset: 'dev',
  apiVersion: '2025-12-05',
  useCdn: false,
};

const sanityProdConfig: SanityConfig = {
  projectId: sanityDevConfig.projectId,
  apiVersion: sanityDevConfig.apiVersion,
  useCdn: true,
  dataset: 'production',
};

const devConfig: MgPrisConfig = {
  ...baseConfig,
  mpe: { url: 'https://mg-price-engine.datahub-dev.mestergruppen.cloud' },
  auth0: {
    clientId: 'UYjBrp5ppcwJUL5b5UxvRdOfmZFs5Buo',
    domain: 'dev.id.mestergruppen.cloud',
    audience: 'mg.pps',
    mpeAudience: 'mg.mpe',
    mgAppsBffAudience: 'mg.mg-apps-bff',
  },
  mgAppsBff: { url: isRunningLocally ? 'http://localhost:8080/api' : 'api' },
  featureFlags: [
    { name: 'help-center', enabled: true },
    {
      name: 'supplier-price-change-notification-config',
      enabled: true,
    },
    { name: 'sales-price-condition', enabled: true },
    { name: 'local_campaigns', enabled: true },
    { name: 'chatbot', enabled: true },
    { name: 'price-rounding', enabled: true },
    { name: 'price-control', enabled: true },
    { name: 'all-conditions', enabled: true },
    { name: 'local-price-suggestions', enabled: true },
    { name: 'new-dashboard', enabled: true },
    { name: 'discount-matrix', enabled: true },
    { name: 'replace-item', enabled: true },
  ],
  gaMeasurementId: 'G-VKQD1381K7',
  sanity: sanityDevConfig,
};

const localDevConfig: MgPrisConfig = {
  ...devConfig,
  mpe: { url: 'http://localhost:8080' },
  gaMeasurementId: undefined,
  sentryDsn: undefined,
};

const stageConfig: MgPrisConfig = {
  ...baseConfig,
  mpe: { url: 'https://mg-price-engine.datahub-stage.mestergruppen.cloud' },
  auth0: {
    clientId: 'ZrXG1bqDMluUcStracHu4ICTb6GOjIxm',
    domain: 'stage.id.mestergruppen.cloud',
    audience: 'mg.pps',
    mpeAudience: 'mg.mpe',
    mgAppsBffAudience: 'mg.mg-apps-bff',
  },
  mgAppsBff: { url: 'api' },
  featureFlags: [
    {
      name: 'help-center',
      enabled: true,
    },
    { name: 'sales-price-condition', enabled: true },
    { name: 'local_campaigns', enabled: true },
    { name: 'chatbot', enabled: true },
    { name: 'price-rounding', enabled: true },
    { name: 'price-control', enabled: true },
    { name: 'all-conditions', enabled: true },
    { name: 'local-price-suggestions', enabled: true },
    { name: 'new-dashboard', enabled: true },
    { name: 'discount-matrix', enabled: false },
    { name: 'replace-item', enabled: true },
  ],
  gaMeasurementId: 'G-L50QSGM4KC',
  clarity: {
    id: 'nva1srvcoy',
  },
  sanity: {
    ...sanityProdConfig,
    useCdn: false,
  },
};

const localStageConfig: MgPrisConfig = {
  ...stageConfig,
  mpe: { url: 'http://localhost:8080' },
  gaMeasurementId: undefined,
  sentryDsn: undefined,
};

const prodConfig: MgPrisConfig = {
  ...baseConfig,
  mpe: { url: 'https://mg-price-engine.datahub-prod.mestergruppen.cloud' },
  auth0: {
    clientId: 'Ee7lS0kFJ2p4etdsMj1rlNyHOnYuTaqE',
    domain: 'id.mestergruppen.cloud',
    audience: 'mg.pps',
    mpeAudience: 'mg.mpe',
    mgAppsBffAudience: 'mg.mg-apps-bff',
  },
  mgAppsBff: { url: 'api' },
  featureFlags: [
    { name: 'help-center', enabled: true },
    { name: 'sales-price-condition', enabled: true },
    { name: 'price-control', enabled: true },
    { name: 'all-conditions', enabled: true },
    {
      name: 'local-price-suggestions',
      enabled: true,
      excludedScopes: [{ orgUnitType: OrgUnitType.WHOLESALE_LOCATION }],
    },
    { name: 'chatbot', enabled: true },
    { name: 'local_campaigns', enabled: false },
    { name: 'price-rounding', enabled: true },
    { name: 'new-dashboard', enabled: true },
    { name: 'replace-item', enabled: false },
  ],
  sanity: {
    projectId: sanityDevConfig.projectId,
    apiVersion: sanityDevConfig.apiVersion,
    useCdn: true,
    dataset: 'production',
  },
  gaMeasurementId: 'G-KWYZQBT33Z',
  clarity: {
    id: 'jmqq9u2kpx',
  },
};

export const mgPrisEnvConfigs: { [key in envType]: MgPrisConfig } = {
  localDev: localDevConfig,
  dev: devConfig,
  localStage: localStageConfig,
  stage: stageConfig,
  prod: prodConfig,
};

export const mgPrisConfig: MgPrisConfig = mgPrisEnvConfigs[env];
